<template>
	<div
		style="touch-action: pan-y"
		:class="{ 'interact--swipe': true, swiping }"
		:swipe-left="swipeLeft"
		:swipe-right="swipeRight"
	>
		<slot />
	</div>
</template>

<script>
import interact from 'interactjs';
import { mapState } from 'vuex';
import { isIOS } from '@/tools/helpers';

interact.pointerMoveTolerance(20);
const safariHistoryBrowseMargin = 45;
export default {
	props: {
		value: Object,
		disabled: Boolean,
		swipeRight: Boolean,
		swipeLeft: Boolean,
	},

	data() {
		return {
			delta: { x: 0, y: 0 },
			swiping: false,
			preventBorderSwipe: true,
		};
	},

	computed: {
		...mapState(['isApp']),
		isSafari() {
			return isIOS() && !this.isApp;
		},
	},

	watch: {
		value(newValue) {
			newValue = newValue || { x: 0, y: 0 };
			this.delta.x = newValue.x;
			this.delta.y = newValue.y;
		},

		disabled(isDisabled) {
			if (isDisabled) this.unset();
			else this.setup();
		},

		swipeLeft() {
			this.enableDisableDrag();
		},

		swipeRight() {
			this.enableDisableDrag();
		},
	},

	methods: {
		reset() {
			this.delta = { x: 0, y: 0 };
			this.$emit('input', this.delta);
		},

		enableDisableDrag() {
			const enabled = this.swipeLeft || this.swipeRight;

			this.startRect = this.$parent.$el.getBoundingClientRect();

			this.restrictOptions.elementRect = {
				left: this.swipeRight ? 1 : 0,
				right: this.swipeLeft ? 0 : 1,
				top: 0,
				bottom: 1,
			};

			this.interactable.draggable({
				enabled,
				modifiers: [
					interact.modifiers.snap(this.snapOptions),
					interact.modifiers.restrict(this.restrictOptions),
				],
			});
		},

		setup() {
			if (this.interactable || !this.$parent) return;

			this.interactable = interact(this.$parent.$el);

			this.snapOptions = {
				endOnly: true,
				relativePoints: [{ x: 0, y: 0 }],
				targets: [
					// snap unchanged
					() => ({ x: 0, range: this.startRect.width / 4 }),

					// snap next
					() => ({ x: -this.startRect.width }),

					// snap previous
					() => ({ x: this.startRect.width }),
				],
			};

			this.restrictOptions = {
				restriction: () => ({ left: 0, right: this.startRect.width, top: 0, bottom: window.innerHeight }),
			};

			this.interactable
				.styleCursor(false)
				.preventDefault('never')
				.on('move', (event) => {
					const { interaction } = event;
					const delta = event._interaction.coords.delta.client;

					if (interaction.interacting() && Math.abs(delta.x) > Math.abs(delta.y)) {
						event.preventDefault();
					}
				})
				.draggable({
					enabled: false,
					startAxis: 'x',
					lockAxis: 'x',

					inertia: {
						smoothEndDuration: 300,
						allowResume: false,
					},
				})
				.on('dragstart', (event) => {
					this.startRect = event.target.getBoundingClientRect();
					if (
						this.isSafari &&
						(event.page.x <= safariHistoryBrowseMargin ||
							event.rect.width - event.page.x <= safariHistoryBrowseMargin)
					) {
						this.preventBorderSwipe = true;
						if (event.interact) event.interact.stop();
					} else {
						this.$parent.$el.style.touchAction = 'none';
						this.swiping = true;
						this.preventBorderSwipe = false;
					}
				})
				.on('dragmove dragstart dragend', (event) => {
					if (!this.preventBorderSwipe) {
						this.delta.x += event.dx;
						this.delta.y += event.dy;
						this.$emit('input', this.delta);

						this.$emit(event.type, event);
					}
				})
				.on('dragend', (event) => {
					/* eslint-disable no-nested-ternary */
					if (!this.preventBorderSwipe) {
						let direction = this.delta.x < 0 ? 'right' : this.delta.x > 0 ? 'left' : null;
						this.$emit('swipe', { direction, event });
						this.$parent.$el.style.touchAction = 'pan-y';
						this.swiping = false;
					}
				});

			this.enableDisableDrag();

			// this.$parent.$el.style.touchAction = 'pan-y';
		},

		unset() {
			if (this.interactable) {
				this.reset();
				this.interactable.unset();
				this.interactable = null;
				this.$parent.$el.style.touchAction = 'pan-Y';
			}
		},
	},

	mounted() {
		this.startRect = {};
		if (!this.disabled) this.setup();
	},

	beforeDestroy() {
		this.unset();
	},
};
</script>
